import React from 'react';
import { Link } from 'react-router-dom';
import BlueCallToAction from '../../components/common/BlueCallToAction';

/**
 * Revenue Cycle Solution Page
 * End-user focused view of Videxa's revenue cycle capabilities
 */
const RevenueCycleSolutionPage = () => {
  const challenges = [
    {
      title: "Complex Billing Workflows",
      description: "Multiple systems, manual processes, and disconnected data create billing bottlenecks.",
      solution: "Unified platform that consolidates all billing data into a single source of truth."
    },
    {
      title: "High Denial Rates",
      description: "Claims denied due to coding errors, missing documentation, or authorization issues.",
      solution: "AI-powered claim scrubbing catches errors before submission."
    },
    {
      title: "Slow Reimbursement",
      description: "Extended days in A/R impacting cash flow and financial planning.",
      solution: "Automated follow-up and intelligent prioritization accelerate collections."
    },
    {
      title: "Limited Visibility",
      description: "Difficulty tracking KPIs and identifying performance issues in real-time.",
      solution: "Real-time dashboards with predictive analytics for proactive management."
    }
  ];

  const capabilities = [
    {
      title: "Charge Capture",
      description: "Ensure all services are captured and coded correctly at the point of care.",
      features: ["Automated charge posting", "CDM management", "Missing charge alerts"]
    },
    {
      title: "Claims Management",
      description: "Submit clean claims the first time with AI-powered validation.",
      features: ["Claim scrubbing", "Edit resolution", "Electronic submission"]
    },
    {
      title: "Denial Management",
      description: "Prevent, track, and appeal denied claims efficiently.",
      features: ["Root cause analysis", "Appeal automation", "Denial trending"]
    },
    {
      title: "Patient Collections",
      description: "Optimize patient financial engagement and payment collection.",
      features: ["Eligibility verification", "Estimates", "Payment plans"]
    },
    {
      title: "Analytics & Reporting",
      description: "Comprehensive visibility into revenue cycle performance.",
      features: ["Real-time KPIs", "Benchmarking", "Predictive insights"]
    },
    {
      title: "Compliance",
      description: "Stay compliant with payer rules and regulatory requirements.",
      features: ["Audit trails", "Documentation", "Regulatory updates"]
    }
  ];

  const metrics = [
    { label: "Reduction in Days in A/R", value: "35%", icon: "📉" },
    { label: "Improvement in Clean Claim Rate", value: "94%", icon: "✓" },
    { label: "Decrease in Denial Rate", value: "65%", icon: "📊" },
    { label: "Faster Payment Posting", value: "50%", icon: "⚡" }
  ];

  const userBenefits = [
    {
      role: "Revenue Cycle Directors",
      benefits: [
        "Complete visibility into organizational KPIs",
        "Predictive analytics for proactive management",
        "Automated reporting and benchmarking"
      ]
    },
    {
      role: "Billing Staff",
      benefits: [
        "Streamlined workflows reduce manual tasks",
        "AI-assisted claim editing and submission",
        "Automated work queue prioritization"
      ]
    },
    {
      role: "CFOs & Finance Leaders",
      benefits: [
        "Improved cash flow and reduced A/R days",
        "Real-time financial dashboards",
        "ROI tracking and performance metrics"
      ]
    }
  ];

  return (
    <div className="revenue-cycle-solution-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              Revenue Cycle <span style={{ color: '#FFC000' }}>Solutions</span>
            </h1>
            <p className="text-xl md:text-2xl mb-4 text-gray-700">
              Optimize your entire revenue cycle with AI-powered automation
            </p>
            <p className="text-lg mb-8 text-gray-600">
              From patient registration to final payment, Videxa streamlines every step of your revenue cycle.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/cdi-trial"
                style={{
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:opacity-90 transition-opacity"
              >
                Start Free Trial
              </Link>
              <Link
                to="/contact"
                style={{
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600'
                }}
                className="hover:bg-[#0E3056] hover:text-white transition-colors"
              >
                Schedule Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-16 bg-[#0E3056] text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">
            Results Our Customers Achieve
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {metrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl mb-2">{metric.icon}</div>
                <div className="text-4xl font-bold text-[#FFC000] mb-2">{metric.value}</div>
                <p className="text-gray-200">{metric.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Addressing Your Challenges
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            We understand the complexities of healthcare revenue cycle management
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {challenges.map((item, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg"
              >
                <h3 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-2">
                  Challenge: {item.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {item.description}
                </p>
                <div className="border-t border-gray-200 dark:border-gray-600 pt-4">
                  <h4 className="text-lg font-semibold text-green-600 dark:text-green-400 mb-2">
                    Our Solution
                  </h4>
                  <p className="text-gray-700 dark:text-gray-200">
                    {item.solution}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Comprehensive Capabilities
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            End-to-end revenue cycle management in a single platform
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md"
              >
                <h3 className="text-xl font-semibold text-[#0E3056] dark:text-[#FFC000] mb-3">
                  {capability.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {capability.description}
                </p>
                <ul className="space-y-2">
                  {capability.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-gray-700 dark:text-gray-300">
                      <span className="text-green-500 mr-2">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* User Benefits */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 text-gray-900 dark:text-white text-center">
            Benefits by Role
          </h2>
          <p className="text-xl text-center text-gray-600 dark:text-gray-300 mb-12 max-w-3xl mx-auto">
            Tailored value for every member of your revenue cycle team
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {userBenefits.map((user, index) => (
              <div
                key={index}
                className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg"
              >
                <h3 className="text-xl font-semibold text-[#0E3056] dark:text-[#FFC000] mb-4">
                  {user.role}
                </h3>
                <ul className="space-y-3">
                  {user.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start text-gray-700 dark:text-gray-300">
                      <span className="text-[#FFC000] mr-2 mt-1">•</span>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <BlueCallToAction
        heading="Ready to transform your revenue cycle?"
        description="Join healthcare organizations achieving significant improvements with Videxa."
        primaryButtonText="Start Free Trial"
        primaryButtonLink="/cdi-trial"
        secondaryButtonText="Schedule Demo"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default RevenueCycleSolutionPage;
